import type { Channel, Character, Easing, Keyframe, SceneObject } from '../../domain/schemas';
import type { Rng } from '../../lib/random';
import { absolutePoints, pathKeys } from './paths';
import { JUMP_POSES, RUN_SWING, WALK_SWING, legPixels, legSwing, runShape, travelPerCycle, walkShape, type CycleShape, type Quarters } from './poses';
import { TEMPLATES, type MotionPath, type ParamValue, type TemplateId } from './spec';

/**
 * The motion library (Phase 3C): each template turns a target's state at the start of a
 * step into keyframes for the frame range [f0, f1]. Templates are pure: they read only
 * their input (no clock, no Pixi, no Math.random — variation comes from the seeded `rng`).
 *
 * Continuity: every value starts from `at(channel)` — the target's displayed value at f0
 * (existing animation, base, or an earlier step of the plan) — and character templates
 * end at the instance's pose from before the step (except `fall`, which ends lying down).
 * So steps placed back to back join without jumps, and a sequence ends in its idle pose.
 */
export interface TemplateInput {
  object: SceneObject;
  character: Character | undefined;
  f0: number;
  f1: number;
  fps: number;
  /** Cycles for cyclic templates (already resolved). */
  cycles: number;
  /** The cycle count was given explicitly ("repeat 4 times"), so locomotion keeps it. */
  explicitCycles: boolean;
  easing: Easing;
  p: Record<string, ParamValue>;
  path: MotionPath | null;
  /** Value of a channel at f0 (before this step changes it). */
  at: (channel: Channel) => number;
  /** Seeded generator when variation > 0, else null. */
  rng: Rng | null;
  variation: number;
  scene: { width: number; height: number };
  warn: (message: string) => void;
}

export type TemplateOutput = Partial<Record<Channel, Keyframe[]>>;

/** Collects keys per channel; later keys on the same frame replace earlier ones. */
class Keys {
  readonly out: TemplateOutput = {};
  constructor(private readonly f0: number, private readonly f1: number) {}
  add(channel: Channel, frame: number, value: number, easing: Easing = 'linear'): void {
    const f = Math.min(this.f1, Math.max(this.f0, Math.round(frame)));
    const keys = (this.out[channel] ??= []);
    const last = keys[keys.length - 1];
    if (last && last.frame === f) keys[keys.length - 1] = { frame: f, value, easing };
    else if (last && last.frame > f) {
      const i = keys.findIndex((k) => k.frame >= f);
      if (keys[i]!.frame === f) keys[i] = { frame: f, value, easing };
      else keys.splice(i, 0, { frame: f, value, easing });
    } else keys.push({ frame: f, value, easing });
  }
}

const num = (v: ParamValue | undefined, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const frameAt = (f0: number, f1: number, t: number) => Math.round(lerp(f0, f1, t));
/** Small multiplicative jitter (1 ± 0.25 · variation) from the seeded generator. */
const jitter = (input: TemplateInput, amount = 0.25) => (input.rng ? 1 + (input.rng.float() * 2 - 1) * amount * input.variation : 1);

/**
 * A cycle keyed at every quarter: value(q) = base + envelope(q) · shape[(q + phase) % 4].
 * With `envelope`, the first and last quarter points sit at the base value, so constant
 * offsets (a lean, a bob) ease in and out and the motion starts and ends at rest.
 * Variation (seeded) jitters each cycle's amplitude and the interior key timing.
 */
function cycle(k: Keys, input: TemplateInput, shape: CycleShape, cycles: number, opts: { phase?: number; envelope?: boolean; easing?: Easing; scale?: number } = {}): void {
  const { f0, f1 } = input;
  const quarters = Math.max(2, Math.round(cycles * 4));
  const phase = opts.phase ?? 0;
  const span = f1 - f0;
  const amps: number[] = [];
  for (let c = 0; c <= Math.ceil(quarters / 4); c++) amps.push(jitter(input));
  const frames: number[] = [];
  for (let q = 0; q <= quarters; q++) {
    let f = f0 + (span * q) / quarters;
    if (input.rng && q > 0 && q < quarters) f += (input.rng.float() * 2 - 1) * 0.2 * input.variation * (span / quarters);
    frames.push(Math.round(f));
  }
  for (const [channel, values] of Object.entries(shape) as [Channel, Quarters][]) {
    const base = input.at(channel);
    let last = -1;
    for (let q = 0; q <= quarters; q++) {
      const f = Math.max(frames[q]!, last + 1);
      if (f > f1 || (q < quarters && f >= f1)) continue;
      last = f;
      const env = opts.envelope && (q === 0 || q === quarters) ? 0 : 1;
      k.add(channel, f, base + env * values[(q + phase) % 4]! * amps[Math.floor(q / 4)]! * (opts.scale ?? 1), opts.easing ?? 'ease-in-out');
    }
    k.add(channel, f1, base + (opts.envelope ? 0 : values[(quarters + phase) % 4]! * (opts.scale ?? 1)), opts.easing ?? 'ease-in-out');
  }
}

/** Sets pose keys relative to the start pose at the given fractions of the step. */
function poseKeys(k: Keys, input: TemplateInput, channel: Channel, points: [number, number][], easing: Easing = 'ease-in-out'): void {
  const base = input.at(channel);
  for (const [t, v] of points) k.add(channel, frameAt(input.f0, input.f1, t), base + v, easing);
}

/** Cycle count for locomotion when travelling `distance` px, adjusting stride to avoid sliding. */
function locomotion(input: TemplateInput, run: boolean): { cycles: number; swing: number; distance: number } {
  const { p, character, object } = input;
  const stride = num(p.stride, 1);
  const base = legSwing(run ? RUN_SWING : WALK_SWING, character) * stride;
  const seconds = (input.f1 - input.f0) / input.fps;
  const natural = 1 / (TEMPLATES[run ? 'run' : 'walk'].cycleSeconds ?? 1);
  const [lo, hi] = run ? [1, 4] : [0.5, 2.5];
  const legPx = legPixels(character, object.height);
  const explicitCycles = input.explicitCycles ? input.cycles : null;
  const half = (c: number) => Math.max(0.5, Math.round(c * 2) / 2);
  if (p.direction === 'none') {
    const cycles = explicitCycles ?? half(num(p.frequency, natural) * seconds);
    return { cycles, swing: base, distance: 0 };
  }
  const wanted = typeof p.distance === 'number' && p.distance > 0 ? p.distance : null;
  if (wanted === null) {
    // Natural speed: the distance follows from the cycles, so feet never slide.
    const cycles = explicitCycles ?? half(num(p.frequency, natural) * seconds);
    return { cycles, swing: base, distance: cycles * travelPerCycle(legPx, base) };
  }
  let cycles = explicitCycles;
  if (cycles === null) {
    const needed = wanted / travelPerCycle(legPx, base) / seconds;
    const frequency = typeof p.frequency === 'number' ? p.frequency : Math.min(hi, Math.max(lo, needed));
    cycles = half(frequency * seconds);
  }
  // Fit the swing so each cycle covers wanted / cycles, within a natural range; when the
  // distance is too short (or long) for that, use fewer (or more) half cycles first.
  const minSwing = base * 0.4;
  const maxSwing = Math.min(base * 1.8, 75);
  const fit = (c: number) => {
    const ratio = wanted / (4 * legPx * c);
    return ratio >= 1 ? 90 : (Math.asin(ratio) * 180) / Math.PI;
  };
  if (explicitCycles === null) {
    while (fit(cycles) < minSwing && cycles > 0.5) cycles -= 0.5;
    while (fit(cycles) > maxSwing && cycles / seconds < hi * 1.5) cycles += 0.5;
  }
  const swing = Math.min(Math.max(minSwing, fit(cycles)), maxSwing);
  const covered = cycles * travelPerCycle(legPx, swing);
  if (Math.abs(covered - wanted) / wanted > 0.1) {
    input.warn(`Feet slide: ${Math.round(wanted)} px in ${seconds.toFixed(2)} s needs ${Math.round((wanted / covered) * 100)}% of the stride the legs can cover. Make the step longer or the distance shorter.`);
  }
  return { cycles, swing, distance: wanted };
}

const dirSign = (d: ParamValue | undefined) => (d === 'left' || d === 'up' || d === 'counterclockwise' ? -1 : 1);

/** Generates the keys of one template for one target over [f0, f1]. */
export function generate(template: TemplateId, input: TemplateInput): TemplateOutput {
  const { f0, f1, p, object, character } = input;
  const k = new Keys(f0, f1);
  const H = object.height;
  const W = object.width;
  const I = num(p.intensity, 1);
  const isCharacter = object.type === 'character' && !!character;
  switch (template) {
    case 'idle':
      cycle(k, input, { 'pose.torsoY': [0, -0.008, 0, 0.004], 'pose.head': [0, 2, 0, -2], 'pose.armL': [0, 2, 0, -2], 'pose.armR': [0, -2, 0, 2] }, input.cycles, { scale: I });
      break;
    case 'walk':
    case 'run': {
      const run = template === 'run';
      const m = locomotion(input, run);
      // Phase 1: legs pass through vertical at the start and end of every half cycle.
      cycle(k, input, run ? runShape(character, m.swing) : walkShape(character, m.swing), m.cycles, { phase: 1, envelope: true });
      if (m.distance > 0) {
        const x0 = input.at('x');
        k.add('x', f0, x0, 'linear');
        k.add('x', f1, x0 + dirSign(p.direction) * m.distance, 'linear');
      }
      break;
    }
    case 'jump': {
      const h = num(p.height, 0.6) * H * jitter(input, 0.2);
      const y0 = input.at('y');
      const t = (f: number) => frameAt(f0, f1, f);
      const takeoff = t(0.3);
      const land = t(0.78);
      k.add('y', f0, y0, 'ease-in-out');
      k.add('y', takeoff, y0, 'linear');
      // Parabolic arc sampled every 2 frames (linear keys between samples).
      for (let f = takeoff + 2; f < land; f += 2) {
        const u = (f - takeoff) / (land - takeoff);
        k.add('y', f, y0 - 4 * h * u * (1 - u), 'linear');
      }
      k.add('y', land, y0, 'linear');
      k.add('y', f1, y0, 'linear');
      const d = typeof p.distance === 'number' && p.direction !== 'none' ? p.distance : 0;
      if (d > 0) {
        const x0 = input.at('x');
        k.add('x', f0, x0, 'linear');
        k.add('x', takeoff, x0, 'linear');
        k.add('x', land, x0 + dirSign(p.direction) * d, 'linear');
        k.add('x', f1, x0 + dirSign(p.direction) * d, 'linear');
      }
      if (isCharacter) {
        // Crouch (anticipation) → take-off → peak → landing compression → recovery.
        for (const [channel, [crouch, up, peak, landing]] of Object.entries(JUMP_POSES) as [Channel, Quarters][]) {
          poseKeys(k, input, channel, [
            [0, 0],
            [0.22, crouch],
            [0.3, up],
            [0.54, peak],
            [0.78, landing],
            [0.86, channel === 'pose.torsoY' ? 0.035 : crouch],
            [1, 0],
          ]);
        }
      } else if (p.squash !== false) {
        const sy = input.at('scaleY');
        const sx = input.at('scaleX');
        for (const [frac, y, x] of [
          [0, 1, 1],
          [0.22, 0.85, 1.1],
          [0.3, 1.08, 0.95],
          [0.54, 1, 1],
          [0.78, 0.85, 1.1],
          [0.9, 1, 1],
          [1, 1, 1],
        ] as const) {
          k.add('scaleY', t(frac), sy * y, 'ease-in-out');
          k.add('scaleX', t(frac), sx * x, 'ease-in-out');
        }
      }
      break;
    }
    case 'land':
      poseKeys(k, input, 'pose.torsoY', [[0, 0], [0.35, 0.04 * I], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.legL', [[0, 0], [0.35, 10 * I], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.legR', [[0, 0], [0.35, -10 * I], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.armL', [[0, 0], [0.35, 35 * I], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.armR', [[0, 0], [0.35, -35 * I], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.head', [[0, 0], [0.35, -4 * I], [1, 0]], 'ease-out');
      break;
    case 'crouch': {
      const hold = Math.min(0.9, Math.max(0, num(p.hold, 0.4)));
      const a = (1 - hold) / 2;
      const down = (v: number): [number, number][] => [[0, 0], [a, v], [1 - a, v], [1, 0]];
      poseKeys(k, input, 'pose.torsoY', down(0.07 * I));
      poseKeys(k, input, 'pose.legL', down(18 * I));
      poseKeys(k, input, 'pose.legR', down(-18 * I));
      poseKeys(k, input, 'pose.head', down(6 * I));
      poseKeys(k, input, 'pose.armL', down(12 * I));
      poseKeys(k, input, 'pose.armR', down(-12 * I));
      break;
    }
    case 'wave': {
      const left = p.arm === 'left';
      const channel: Channel = left ? 'pose.armL' : 'pose.armR';
      const s = left ? 1 : -1;
      const up = Math.min(165, 145 * I);
      const amp = Math.min(30, 22 * I);
      const waves = Math.max(1, Math.round(num(p.waves, 3)));
      const points: [number, number][] = [[0, 0], [0.2, s * up]];
      for (let i = 1; i <= waves * 2; i++) points.push([0.2 + (0.6 * i) / (waves * 2), s * (up + (i % 2 === 1 ? amp : -amp) * jitter(input))]);
      points[points.length - 1] = [0.8, s * up];
      points.push([1, 0]);
      poseKeys(k, input, channel, points);
      poseKeys(k, input, 'pose.head', [[0, 0], [0.2, s * -5], [0.8, s * -5], [1, 0]]);
      break;
    }
    case 'turn': {
      const sx = input.at('scaleX');
      k.add('scaleX', f0, sx, 'ease-in');
      k.add('scaleX', frameAt(f0, f1, 0.5), sx * 0.08, 'ease-out');
      k.add('scaleX', f1, sx, 'ease-out');
      if (isCharacter) poseKeys(k, input, 'pose.head', [[0, 0], [0.5, 14], [1, 0]]);
      break;
    }
    case 'celebrate': {
      const hops = Math.max(0, Math.round(num(p.hops, 2)));
      poseKeys(k, input, 'pose.armL', [[0, 0], [0.18, 155 * Math.min(1.1, I)], [0.85, 155 * Math.min(1.1, I)], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.armR', [[0, 0], [0.18, -155 * Math.min(1.1, I)], [0.85, -155 * Math.min(1.1, I)], [1, 0]], 'ease-out');
      poseKeys(k, input, 'pose.head', [[0, 0], [0.18, -6], [0.85, -6], [1, 0]]);
      const y0 = input.at('y');
      k.add('y', f0, y0, 'ease-out');
      if (hops > 0) {
        const a = 0.2;
        const b = 0.85;
        for (let i = 0; i < hops; i++) {
          const s = a + ((b - a) * i) / hops;
          const e = a + ((b - a) * (i + 1)) / hops;
          k.add('y', frameAt(f0, f1, s), y0, 'ease-out');
          k.add('y', frameAt(f0, f1, (s + e) / 2), y0 - 0.12 * H * I * jitter(input), 'ease-in');
          k.add('y', frameAt(f0, f1, e), y0, 'ease-out');
        }
      }
      k.add('y', f1, y0, 'ease-out');
      break;
    }
    case 'fall': {
      const s = dirSign(p.direction);
      const r0 = input.at('rotation');
      const y0 = input.at('y');
      const lying = y0 + H - W / 2 - object.anchorY * H;
      k.add('rotation', f0, r0, 'ease-in');
      k.add('rotation', frameAt(f0, f1, 0.8), r0 + s * 90, 'ease-out');
      k.add('rotation', frameAt(f0, f1, 0.9), r0 + s * 84, 'ease-in');
      k.add('rotation', f1, r0 + s * 90, 'linear');
      k.add('y', f0, y0, 'ease-in');
      k.add('y', frameAt(f0, f1, 0.8), lying, 'linear');
      k.add('y', f1, lying, 'linear');
      if (isCharacter) {
        poseKeys(k, input, 'pose.armL', [[0, 0], [0.4, 120], [1, 80]]);
        poseKeys(k, input, 'pose.armR', [[0, 0], [0.4, -120], [1, -80]]);
        poseKeys(k, input, 'pose.head', [[0, 0], [0.4, -12], [1, 0]]);
      }
      break;
    }
    case 'float': {
      const amp = 0.05 * H * I;
      cycle(k, input, { y: [0, -amp, 0, amp], rotation: [0, 3 * I, 0, -3 * I] }, input.cycles);
      break;
    }
    case 'sway': {
      const a = num(p.angle, 8);
      cycle(k, input, { rotation: [0, a, 0, -a] }, input.cycles);
      break;
    }
    case 'bounce': {
      const y0 = input.at('y');
      const sy = input.at('scaleY');
      const n = Math.max(1, Math.round(input.cycles));
      let h = num(p.height, 0.4) * H;
      for (let i = 0; i < n; i++) {
        const a = frameAt(f0, f1, i / n);
        const b = frameAt(f0, f1, (i + 1) / n);
        if (b - a < 2) continue;
        k.add('y', a, y0, 'ease-out');
        k.add('y', Math.round((a + b) / 2), y0 - h * jitter(input, 0.2), 'ease-in');
        if (p.squash !== false) {
          k.add('scaleY', a, i === 0 ? sy : sy * 0.85, 'ease-out');
          k.add('scaleY', a + Math.max(1, Math.round((b - a) * 0.15)), sy, 'linear');
        }
        if (p.decay === true) h *= 0.6;
      }
      k.add('y', f1, y0, 'linear');
      if (p.squash !== false) k.add('scaleY', f1, sy, 'linear');
      break;
    }
    case 'spin': {
      const r0 = input.at('rotation');
      const turns = num(p.turns, 1) * input.cycles;
      k.add('rotation', f0, r0, input.easing);
      k.add('rotation', f1, r0 + dirSign(p.direction) * 360 * turns, input.easing);
      break;
    }
    case 'shake': {
      const amp = num(p.amplitude, 8);
      const interval = Math.max(1, Math.round(input.fps / (2 * num(p.frequency, 12))));
      const axes: Channel[] = p.axis === 'y' ? ['y'] : p.axis === 'both' ? ['x', 'y'] : ['x'];
      for (const [ai, channel] of axes.entries()) {
        const v0 = input.at(channel);
        k.add(channel, f0, v0, 'linear');
        let i = 0;
        for (let f = f0 + interval; f < f1; f += interval, i++) {
          const decay = p.decay === false ? 1 : 1 - (f - f0) / (f1 - f0);
          const sign = (i + ai) % 2 === 0 ? 1 : -1;
          k.add(channel, f, v0 + sign * amp * decay * jitter(input, 0.6), 'linear');
        }
        k.add(channel, f1, v0, 'linear');
      }
      break;
    }
    case 'pulse': {
      const amount = num(p.amount, 0.15);
      const sx = input.at('scaleX');
      const sy = input.at('scaleY');
      const o0 = input.at('opacity');
      const n = Math.max(1, Math.round(input.cycles));
      for (let i = 0; i < n; i++) {
        const a = frameAt(f0, f1, i / n);
        const mid = frameAt(f0, f1, (i + 0.5) / n);
        const g = 1 + amount * jitter(input);
        k.add('scaleX', a, sx, 'ease-in-out');
        k.add('scaleY', a, sy, 'ease-in-out');
        k.add('scaleX', mid, sx * g, 'ease-in-out');
        k.add('scaleY', mid, sy * g, 'ease-in-out');
        if (p.flicker === true) {
          k.add('opacity', a, o0, 'ease-in-out');
          k.add('opacity', mid, o0 * 0.5, 'ease-in-out');
        }
      }
      k.add('scaleX', f1, sx, 'ease-in-out');
      k.add('scaleY', f1, sy, 'ease-in-out');
      if (p.flicker === true) k.add('opacity', f1, o0, 'ease-in-out');
      break;
    }
    case 'fadeIn': {
      const base = object.opacity > 0 ? object.opacity : 1;
      const to = typeof p.to === 'number' ? p.to : base;
      k.add('opacity', f0, 0, input.easing);
      k.add('opacity', f1, to, input.easing);
      if (input.at('visible') < 0.5) k.add('visible', f0, 1, 'step');
      break;
    }
    case 'fadeOut':
      k.add('opacity', f0, input.at('opacity'), input.easing);
      k.add('opacity', f1, num(p.to, 0), input.easing);
      break;
    case 'slide': {
      const x0 = typeof p.fromX === 'number' ? p.fromX : input.at('x');
      const y0 = typeof p.fromY === 'number' ? p.fromY : input.at('y');
      let x1 = x0;
      let y1 = y0;
      if (p.mode === 'absolute') {
        if (typeof p.toX !== 'number' && typeof p.toY !== 'number') input.warn('Absolute slide has no target position (set To x and/or To y); nothing moves.');
        if (typeof p.toX === 'number') x1 = p.toX;
        if (typeof p.toY === 'number') y1 = p.toY;
      } else {
        const horizontal = p.direction === 'left' || p.direction === 'right';
        const d = typeof p.distance === 'number' && p.distance > 0 ? p.distance : Math.round((horizontal ? input.scene.width : input.scene.height) * 0.25);
        if (horizontal) x1 = x0 + dirSign(p.direction) * d;
        else y1 = y0 + dirSign(p.direction) * d;
      }
      if (x1 !== x0 || typeof p.fromX === 'number') {
        k.add('x', f0, x0, input.easing);
        k.add('x', f1, x1, input.easing);
      }
      if (y1 !== y0 || typeof p.fromY === 'number') {
        k.add('y', f0, y0, input.easing);
        k.add('y', f1, y1, input.easing);
      }
      break;
    }
    case 'path': {
      const path = input.path!;
      const ax = object.anchorX * W;
      const ay = object.anchorY * H;
      const start = { x: input.at('x') + ax, y: input.at('y') + ay };
      const pts = absolutePoints(path, start);
      const gap = Math.hypot(pts[0]!.x - start.x, pts[0]!.y - start.y);
      if (gap > 1) input.warn(`The path starts ${Math.round(gap)} px away from “${object.name}”, so it jumps to the path's start at frame ${f0}.`);
      const r0 = input.at('rotation');
      const { keys } = pathKeys(path.kind, pts, f0, f1, Math.max(1, Math.round(input.cycles)), input.easing, path.orient);
      for (const key of keys) {
        k.add('x', key.frame, key.x - ax, 'linear');
        k.add('y', key.frame, key.y - ay, 'linear');
        if (path.orient) k.add('rotation', key.frame, r0 + key.angle, 'linear');
      }
      break;
    }
  }
  return k.out;
}
